/**
 * TIPOS HOMÔNIMOS UNIFICADOS — por codsite.
 *
 * A query `tipos` do site-graphql (`listTipos`) devolve os tipos DISTINCT dos imóveis
 * publicados, por ID. No legado convivem o tipo global (`tbl_imovel_tipo.CodSite = 0`) e o
 * tipo próprio do tenant com o MESMO nome; quando o acervo mistura os dois, o select de tipos
 * mostra o nome duas vezes e a listagem diz "Apartamentos e Apartamentos à venda".
 *
 * cs2644 (AE Consultimóveis — aeconsultimoveis.com.br), pedido do cliente em 09/10/2026:
 * a Órulo grava "Apartamento" no tipo global (id 1) e a NonStop/CRM no tipo do tenant
 * (id 384088). Cada imóvel da Órulo que a imobiliária ativa na curadoria entra com o tipo
 * global — por isso a regra é por NOME (vale para "Studio", "Cobertura"… no dia em que
 * acontecer), e não um mapa de ids como o de `utils/tiposBuscaOcultos.ts`.
 *
 * Como funciona: o payload do site sai com UM tipo por nome (o primeiro da lista), que leva
 * em `ids_homonimos` os ids de todos os xarás. Tudo que mostra ou resolve tipo (selects, URL,
 * título, sitemap) passa a ver um só. A CONSULTA devolve os xarás com `idsDeTiposParaBusca`:
 * sem isso os imóveis do tipo escondido sumiriam do filtro.
 *
 * Allowlist por codsite: nos demais tenants o payload volta intacto (a MESMA referência) e
 * nenhum tipo tem `ids_homonimos`, então os helpers abaixo devolvem o que receberam.
 */
export const TIPOS_HOMONIMOS_UNIFICADOS: ReadonlySet<number> = new Set<number>([2644]);

export const unificaTiposHomonimos = (codsite: unknown): boolean =>
    TIPOS_HOMONIMOS_UNIFICADOS.has(Number(codsite) || 0);

const chaveDoNome = (nome: unknown) =>
    String(nome ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

const mesmoId = (a: unknown, b: unknown) => Number(a) > 0 && Number(a) === Number(b);

/**
 * Devolve o payload do site com um tipo por nome (clona só se houver xará). Idempotente:
 * payload já unificado não tem nome repetido e volta como veio, com os `ids_homonimos`.
 */
export function unificarTiposHomonimosDoSite<T extends { site?: any; tipos?: any }>(payload: T): T {
    if (!unificaTiposHomonimos(payload?.site?.id) || !Array.isArray(payload?.tipos)) return payload;

    const principalPorNome = new Map<string, any>();
    const tipos: any[] = [];
    let mudou = false;
    for (const tipo of payload.tipos) {
        const chave = chaveDoNome(tipo?.nome);
        const id = Number(tipo?.id);
        const principal = chave && id > 0 ? principalPorNome.get(chave) : undefined;
        if (!principal) {
            const copia = { ...tipo };
            if (chave && id > 0) principalPorNome.set(chave, copia);
            tipos.push(copia);
            continue;
        }
        const xaras: number[] = principal.ids_homonimos || [Number(principal.id)];
        principal.ids_homonimos = xaras.some((x) => mesmoId(x, id)) ? xaras : [...xaras, id];
        mudou = true;
    }
    return mudou ? { ...payload, tipos } : payload;
}

/** Ids escolhidos no filtro → ids que vão na CONSULTA (o tipo unificado leva os xarás junto). */
export function idsDeTiposParaBusca(ids: any[], tipos: any): any[] {
    if (!Array.isArray(ids) || !ids.length || !Array.isArray(tipos)) return ids;

    let saida: any[] | null = null;
    for (const tipo of tipos) {
        const xaras = tipo?.ids_homonimos;
        if (!Array.isArray(xaras) || !ids.some((id) => mesmoId(id, tipo?.id))) continue;
        for (const xara of xaras) {
            if ((saida || ids).some((id) => mesmoId(id, xara))) continue;
            saida = saida || [...ids];
            saida.push(xara);
        }
    }
    return saida || ids;
}

/**
 * Tipo unificado que absorveu este id (o xará escondido). Fallback de quem procura o tipo
 * pelo id do imóvel/empreendimento: `tipos.find(porId) || tipoPorIdHomonimo(tipos, id)`.
 */
export function tipoPorIdHomonimo(tipos: any, id: unknown): any {
    if (!Array.isArray(tipos) || !(Number(id) > 0)) return undefined;
    return tipos.find(
        (tipo: any) => Array.isArray(tipo?.ids_homonimos) && tipo.ids_homonimos.some((x: unknown) => mesmoId(x, id))
    );
}

/**
 * Nomes de tipo sem repetição, para o título da listagem: link antigo (`/apartamento+apartamento`,
 * já compartilhado/indexado) continua chegando com o nome duas vezes na URL.
 */
export function semTiposRepetidos(nomes: string[], codsite: unknown): string[] {
    if (!unificaTiposHomonimos(codsite) || !Array.isArray(nomes) || nomes.length < 2) return nomes;
    const vistos = new Set<string>();
    return nomes.filter((nome) => {
        const chave = chaveDoNome(nome);
        if (vistos.has(chave)) return false;
        vistos.add(chave);
        return true;
    });
}

/**
 * LISTAGEM (/imoveis) de TODOS os sites — Daniel 21/10: "corrigir a duplicação dos tipos na listagem".
 * Fora da allowlist acima o payload continua com os xarás (cada um com o seu id), e a listagem repetia o
 * nome no select, nos chips, no breadcrumb e no título ("Apartamento, Apartamento e Casa à venda").
 * Só a listagem passa a mostrar um tipo por nome; a CONSULTA leva os ids de todos os xarás do nome
 * escolhido (`idsComXarasPorNome`), então os resultados são os mesmos de antes. O resto do site (home,
 * calculadora, condomínios…) não muda. No tenant unificado as duas são no-op (a lista já vem sem xarás).
 */
/** Opções `{ label, value }` com um tipo por nome (fica o primeiro, na ordem recebida). */
export function umTipoPorNome<T extends { label?: any }>(opcoes: T[]): T[] {
    if (!Array.isArray(opcoes) || opcoes.length < 2) return opcoes || [];
    const vistos = new Set<string>();
    const saida = opcoes.filter((opcao) => {
        const chave = chaveDoNome(opcao?.label);
        if (!chave) return true;
        if (vistos.has(chave)) return false;
        vistos.add(chave);
        return true;
    });
    return saida.length === opcoes.length ? opcoes : saida;
}

/** Ids escolhidos → + os ids dos tipos de MESMO NOME em `opcoes` (a lista inteira, sem poda). */
export function idsComXarasPorNome(ids: any[], opcoes: Array<{ label?: any; value?: any }>): any[] {
    if (!Array.isArray(ids) || !ids.length || !Array.isArray(opcoes) || !opcoes.length) return ids;
    const nomes = new Set<string>();
    for (const opcao of opcoes) {
        if (ids.some((id) => mesmoId(id, opcao?.value))) nomes.add(chaveDoNome(opcao?.label));
    }
    nomes.delete('');
    let saida: any[] | null = null;
    for (const opcao of opcoes) {
        if (!nomes.has(chaveDoNome(opcao?.label)) || (saida || ids).some((id) => mesmoId(id, opcao?.value))) continue;
        saida = saida || [...ids];
        saida.push(opcao.value);
    }
    return saida || ids;
}

/**
 * Junta as respostas de `empreendimentos_busca` quando a consulta aceita UM tipo por vez
 * (/condominios): no tipo unificado a página busca cada xará e mostra tudo junto.
 * `temMais` = alguma das buscas ainda tem página (a soma dos totais não serve para decidir o
 * "ver mais"). O total desconta o empreendimento que veio nas duas listas desta página.
 */
export function juntarBuscasDeEmpreendimentos(respostas: any[], pagina: number, porPagina: number): any {
    const vistos = new Set<any>();
    const empreendimentos: any[] = [];
    let count = 0;
    let temMais = false;
    let respondeu = false;
    for (const resposta of respostas) {
        const busca = resposta?.empreendimentos_busca;
        if (!busca || !Array.isArray(busca.empreendimentos)) continue;
        respondeu = true;
        const total = Number(busca.count || 0);
        count += total;
        if (total > pagina * porPagina || busca.empreendimentos.length >= porPagina) temMais = true;
        for (const item of busca.empreendimentos) {
            if (item?.id != null && vistos.has(item.id)) {
                count -= 1;
                continue;
            }
            vistos.add(item?.id);
            empreendimentos.push(item);
        }
    }
    return respondeu ? { empreendimentos_busca: { count, empreendimentos, temMais } } : null;
}
